import { useState } from "react";
import DOMPurify from "dompurify";
import { useMsal } from "@azure/msal-react";

import { DataPoints, getDocument } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import { parseSupportingContentItem } from "./SupportingContentParser";

import styles from "./SupportingContent.module.css";

interface Props {
    supportingContent?: DataPoints;
}

export const SupportingContent = ({ supportingContent }: Props) => {
    const [selectedDoc, setSelectedDoc] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const client = useLogin ? useMsal().instance : undefined;

    const textItems = supportingContent?.text ?? [];
    const imageItems = supportingContent?.images ?? [];
    const webItems = supportingContent?.external_results_metadata ?? [];

    const handleCitationClick = async (title: string) => {
        // title is the AssessmentId from parseSupportingContentItem
        setLoading(true);
        try {
            const token = client ? await getToken(client) : undefined;
            const fullDoc = await getDocument(title, token);
            setSelectedDoc(fullDoc);
        } catch (e) {
            console.error("Failed to fetch document:", e);
        }
        setLoading(false);
    };

    return (
        <div>
            {selectedDoc && (
                <div className={styles.fullNotePanel}>
                    <button onClick={() => setSelectedDoc(null)}>← Back to list</button>
                    <h3>
                        {selectedDoc.source} - {selectedDoc.noteType}
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDoc.content) }} />
                </div>
            )}
            {!selectedDoc && (
                <ul className={styles.supportingContentNavList}>
                    {textItems.map((c, ind) => {
                        const parsed = parseSupportingContentItem(c);
                        return (
                            <li className={styles.supportingContentItem} key={`supporting-content-text-${ind}`}>
                                <h4
                                    className={styles.supportingContentItemHeader}
                                    onClick={() => handleCitationClick(parsed.title)}
                                    style={{ cursor: "pointer", color: "#0078d4" }}
                                >
                                    {parsed.title}
                                </h4>
                                <p className={styles.supportingContentItemText} dangerouslySetInnerHTML={{ __html: parsed.content }} />
                            </li>
                        );
                    })}
                    {imageItems?.map((img, ind) => (
                        <li className={styles.supportingContentItem} key={`supporting-content-image-${ind}`}>
                            <img className={styles.supportingContentItemImage} src={img} alt="Supporting content" />
                        </li>
                    ))}
                    {webItems.map((item, ind) => (
                        <li className={styles.supportingContentItem} key={`supporting-content-web-${item.id ?? ind}`}>
                            {item.url ? (
                                <h4 className={styles.supportingContentItemHeader}>
                                    <a href={item.url} target="_blank" rel="noreferrer">
                                        {item.title ?? item.url}
                                    </a>
                                </h4>
                            ) : (
                                <h4 className={styles.supportingContentItemHeader}>{item.title ?? "Web result"}</h4>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {loading && <p>Loading full note...</p>}
        </div>
    );
};
